import React, { useState, useEffect, createRef } from 'react'
import moment, { Moment } from 'moment'
import { useMutation } from '@apollo/client'
import { useQuery } from '@apollo/client'
import { useSession } from '@slerp/accounts'
import {
  message,
  Button,
  Col,
  Row,
  DatePicker,
  Form,
  Alert,
  PageHeader,
  Tooltip
} from 'antd'
import { orderAbilities } from 'components/Orders/Info/utils'
import { EditOutlined } from '@ant-design/icons'
import {
  GET_TIMESLOTS,
  UPDATE_ORDER_TIMESLOT
} from 'components/Orders/OrderQueries'
import { OrderInfo } from 'components/Orders/Info/type'
import { disabledDate } from 'components/Orders/Helpers'
import { humanizeTimeslot } from './utils'
import { DeliveryCompleteCTA } from 'components/Orders/Info/CallToAction'
import styled from '@emotion/styled'

const { useForm } = Form

export interface Timeslot {
  value: string
  range: string
}

interface FormTypes {
  fulfillmentDate?: Moment
}

interface FulfillmentTimeSlotProps {
  order: OrderInfo
  successCallback: () => void
}

const FulfillmentTimeSlot = ({
  order,
  successCallback
}: FulfillmentTimeSlotProps) => {
  const dropdown = createRef<HTMLSelectElement>()
  const { user } = useSession()
  const { transaction_id, store, merchant } = order
  const { canManageFulfillmentDate } = orderAbilities(order)
  const [form] = useForm()
  const [showForm, setShowForm] = useState<boolean>(false)
  const [newFulfillmentDate, setNewFulfillmentDate] = useState<Date | null>(
    moment().toDate()
  )
  const [timeslots, setTimeslots] = useState<Timeslot[]>([])
  const [selectedTimeslot, setSelectedTimeslot] = useState<string>('')

  const stringDate = (dateTime: Date | null) => {
    if (dateTime) return moment(dateTime).format('YYYY-MM-DD')
    return null
  }

  const { refetch: fetchSlots } = useQuery(GET_TIMESLOTS, {
    variables: {
      storeId: order.store.id,
      fulfillmentDate: stringDate(newFulfillmentDate)
    },
    onCompleted: (data) => {
      if (data?.getFixedStoreTimeslots.length) {
        form.setFieldsValue({
          delivery_time: data.getFixedStoreTimeslots[0].value || 'asap'
        })
      }
    }
  })

  const [updateOrderTimeslot] = useMutation(UPDATE_ORDER_TIMESLOT, {
    fetchPolicy: 'no-cache'
  })

  useEffect(() => {
    if (!newFulfillmentDate || !fetchSlots) return setTimeslots([])

    fetchSlots({
      storeId: order.store.id,
      fulfillmentDate: stringDate(newFulfillmentDate)
    })
      .then((response) => {
        const slots = response?.data?.getFixedStoreTimeslots?.map(
          (slot: any) => {
            if (slot.value == 'asap') {
              return { value: 'immediately', range: 'ASAP' }
            } else {
              return {
                value: moment(slot.value).toISOString(),
                range: humanizeTimeslot(slot)
              }
            }
          }
        )

        setTimeslots(slots || [])
      })
      .catch((error) => {
        console.error('Error during timeslot fetch:', error)
      })
  }, [newFulfillmentDate, store])

  const updateOrderTimeslotHandler = (values: FormTypes) => {
    const { fulfillmentDate } = values

    updateOrderTimeslot({
      variables: {
        transactionId: transaction_id,
        fulfillmentDate:
          fulfillmentDate?.format('YYYY-MM-DD') ||
          moment(newFulfillmentDate).format('YYYY-MM-DD'),
        // used state as i cant seem to pull value for fulfillmentTime from the form
        fulfillmentTime:
          selectedTimeslot != 'immediately'
            ? moment(selectedTimeslot).utc().format('HH:mm')
            : selectedTimeslot,
        userId: user?.id
      }
    })
      .then((response) => {
        message.destroy()
        message.success(
          `Timeslot has been updated for order ${transaction_id}!`,
          1
        )
        successCallback()
      })
      .catch((error) => {
        message.destroy()
        message.error(`${error.message}`, 1)
      })
  }

  return (
    <>
      {showForm ? (
        <Form
          form={form}
          // ref={formRef}
          onFinish={updateOrderTimeslotHandler}
        >
          <PageHeader title='Change Fulfilment Time' />
          <Row gutter={[8, 16]}>
            <Col>
              <Form.Item name='fulfillmentDate' label='Date'>
                <DatePicker
                  format='DD-MM-YYYY'
                  disabledDate={(value) =>
                    disabledDate(
                      value.toDate(),
                      moment().toDate(),
                      //@ts-ignore
                      merchant?.pre_order_settings[0],
                      order
                    )
                  }
                  defaultValue={moment(newFulfillmentDate)}
                  value={moment(newFulfillmentDate)}
                  placeholder='Start date'
                  style={{ width: 170 }}
                  onChange={(value) => {
                    setNewFulfillmentDate(value ? value.toDate() : value)
                  }}
                />
              </Form.Item>
            </Col>
            <Col>
              <Form.Item name='fulfillmentTime' label='Time'>
                {/* had issues with changing select value when date is changed when I used antd's Select component */}
                <select
                  ref={dropdown}
                  name='fulfillmentTime'
                  style={{
                    padding: '4px 11px',
                    borderColor: '#d9d9d9',
                    background: 'white',
                    borderRadius: '2px'
                  }}
                  defaultValue={''}
                  value={selectedTimeslot}
                  onChange={(e) => setSelectedTimeslot(e.target.value)}
                  required
                >
                  <option key='disabledOption' value={''}>
                    {timeslots?.length > 0
                      ? 'Select timeslot'
                      : 'No available timeslots'}
                  </option>
                  {timeslots &&
                    timeslots.length > 0 &&
                    timeslots.map((slot: Timeslot, index: number) => (
                      <option key={index} value={slot.value}>
                        {slot.range}
                      </option>
                    ))}
                </select>
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Alert
              message='Note, if you use SlerpComposer and have already imported your order, you need to change the date in SlerpComposer as well.'
              description={
                <>
                  <span>
                    To learn more about Composer please click{' '}
                    <a
                      target='_blank'
                      rel='noopener noreferrer'
                      href='https://www.slerp.com/project/ordermanagement-composer/'
                    >
                      here
                    </a>
                  </span>
                </>
              }
              type='warning'
              showIcon
            />
          </Row>
          <Row justify='end' gutter={16} style={{ margin: 10 }}>
            <Col>
              <Button
                className='-secondary _uppercase'
                type='ghost'
                onClick={() => setShowForm(false)}
              >
                Cancel
              </Button>
            </Col>
            <Col>
              {!newFulfillmentDate ||
              !selectedTimeslot ||
              !canManageFulfillmentDate ? (
                <Tooltip title='This functionality is not available anymore. Please contact Customer Support for further assistance.'>
                  <Button
                    className='-secondary _uppercase'
                    key='submit'
                    htmlType='submit'
                    disabled={true}
                  >
                    Submit
                  </Button>
                </Tooltip>
              ) : (
                <Button
                  className='-secondary _uppercase'
                  key='submit'
                  htmlType='submit'
                >
                  Submit
                </Button>
              )}
            </Col>
          </Row>
        </Form>
      ) : (
        <ButtonBox>
          {canManageFulfillmentDate ? (
            <Button icon={<EditOutlined />} onClick={() => setShowForm(true)}>
              Change fulfillment time
            </Button>
          ) : (
            <Tooltip title='This functionality is not available anymore. Please contact Customer Support for further assistance.'>
              <Button icon={<EditOutlined />} type='primary' disabled={true}>
                Change fulfillment time
              </Button>
            </Tooltip>
          )}
          <DeliveryCompleteCTA
            order={order}
            successCallback={successCallback}
          />
        </ButtonBox>
      )}
    </>
  )
}

const ButtonBox = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

export default FulfillmentTimeSlot
